'use client';

import React from 'react';
import NextImage, { ImageLoaderProps } from 'next/image';
import { useResolvedLocalizedObject } from 'helpers/hooks/useResolvedLocalizedObject';
import useDimensions from './hooks/useDimensions';
import useParameterizedSrc from './hooks/useParameterizedSrc';
import cloudinaryLoader from './loaders/cloudinary';
import defaultLoader from './loaders/default';
import { ImageProps } from './types';

const Image = ({ media, ratio, gravity, suffix, src, width, height, alt = '', title, ...props }: ImageProps) => {
  const parameterizedSrc = useParameterizedSrc({ ratio, gravity, suffix, media, src });

  const dimensions = useDimensions({ media, width, height, ...props });

  const resovledTitle = useResolvedLocalizedObject(title ?? '');

  const resolvedAlt = useResolvedLocalizedObject(alt ?? '');

  if (!media?.mediaId) {
    // `defaultLoader` ignores `width` (these product/CDN images aren't resized per size), so
    // next/image would emit a width `srcSet` of the SAME url repeated for every deviceSize — pure
    // HTML bloat with zero responsive benefit (all candidates identical to `src`). Pre-resolve the
    // final url (query strip + optional `suffix`) and render `unoptimized` so we get a single
    // <img src> and NO srcSet. `loader` is kept (and is idempotent here) to satisfy
    // images.loader:'custom'.
    const resolvedSrc = defaultLoader({ src: parameterizedSrc, width: 0 } as ImageLoaderProps);
    return (
      <NextImage
        src={resolvedSrc}
        loader={defaultLoader}
        unoptimized
        alt={resolvedAlt}
        title={resovledTitle}
        {...dimensions}
        {...props}
      />
    );
  }

  return (
    <NextImage
      src={parameterizedSrc}
      loader={cloudinaryLoader}
      alt={resolvedAlt}
      title={resovledTitle}
      {...dimensions}
      {...props}
    />
  );
};

export default Image;

export * from './types';
